import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Navigate } from 'react-router-dom';
import { movingHome } from '../../utils/movingTenant';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { ArrowDownToLine, ArrowUpFromLine, MapPin, Package } from 'lucide-react';
import { ShipmentDashboard } from './ShipmentDashboard';
import { ExportsDashboard } from './ExportsDashboard';
import { LocalDashboard } from './LocalDashboard';

export const DashboardWrapper = () => {
  const { user } = useAuth();
  const home = movingHome(user);
  // Moving-company back office never sees the container Imports/Exports dashboard (unless enabled).
  if (home) return <Navigate to={home} replace />;
  return <ShipmentsDashboardWrapper />;
};

const ShipmentsDashboardWrapper = () => {
  const { api } = useAuth();
  const { t } = useTranslation();
  const [dashboardType, setDashboardType] = useState('imports');
  const [masterData, setMasterData] = useState({
    ssls: [],
    terminals: [],
    yards: [],
    drivers: [],
    customers: [],
    chassis_types: [],
    container_types: []
  });
  const [customers, setCustomers] = useState([]);

  const fetchMasterData = useCallback(async () => {
    try {
      const [
        sslsRes, terminalsRes, yardsRes, driversRes, customersRes,
        chassisRes, containerRes
      ] = await Promise.all([
        api.get('/master/ssls').catch(() => ({ data: [] })),
        api.get('/master/terminals').catch(() => ({ data: [] })),
        api.get('/master/yards').catch(() => ({ data: [] })),
        api.get('/master/drivers').catch(() => ({ data: [] })),
        api.get('/customers').catch(() => ({ data: [] })),
        api.get('/master/chassis-types').catch(() => ({ data: [] })),
        api.get('/master/container-types').catch(() => ({ data: [] }))
      ]);

      setMasterData({
        ssls: sslsRes.data || [],
        terminals: terminalsRes.data || [],
        yards: yardsRes.data || [],
        drivers: driversRes.data || [],
        customers: customersRes.data || [],
        chassis_types: chassisRes.data || [],
        container_types: containerRes.data || []
      });
      setCustomers(customersRes.data || []);
    } catch (error) {
      console.error('Failed to fetch master data:', error);
    }
  }, [api]);

  useEffect(() => {
    let mounted = true;
    
    const loadData = async () => {
      if (mounted) {
        await fetchMasterData();
      }
    };
    
    loadData();
    
    return () => {
      mounted = false;
    };
  }, [fetchMasterData]);

  // Dashboard type toggle component
  const DashboardToggle = () => (
    <div className="flex items-center gap-2 mb-4">
      <div className="flex items-center gap-1 mr-4">
        <Package className="h-5 w-5 text-slate-400" />
        <span className="text-slate-400 font-medium">{t('shipments.title')}</span>
      </div>
      <div className="flex items-center gap-1 bg-slate-800/50 p-1 rounded-lg">
        <Button
          onClick={() => setDashboardType('imports')}
          className={dashboardType === 'imports' 
            ? 'bg-blue-600 hover:bg-blue-500 text-white' 
            : 'bg-transparent hover:bg-slate-700 text-slate-400'
          }
          size="sm"
          data-testid="imports-dashboard-btn"
        >
          <ArrowDownToLine className="h-4 w-4 mr-2" />
          {t('dashboard.imports', 'Imports')}
        </Button>
        <Button
          onClick={() => setDashboardType('exports')}
          className={dashboardType === 'exports' 
            ? 'bg-green-600 hover:bg-green-500 text-white' 
            : 'bg-transparent hover:bg-slate-700 text-slate-400'
          }
          size="sm"
          data-testid="exports-dashboard-btn"
        >
          <ArrowUpFromLine className="h-4 w-4 mr-2" />
          {t('dashboard.exports', 'Exports')}
        </Button>
        <Button
          onClick={() => setDashboardType('local')}
          className={dashboardType === 'local' 
            ? 'bg-orange-600 hover:bg-orange-500 text-white' 
            : 'bg-transparent hover:bg-slate-700 text-slate-400'
          }
          size="sm"
          data-testid="local-dashboard-btn"
        >
          <MapPin className="h-4 w-4 mr-2" />
          {t('dashboard.local', 'Local')}
        </Button>
      </div>
    </div>
  );

  // Render based on dashboard type
  if (dashboardType === 'imports') {
    return (
      <div className="space-y-4">
        <DashboardToggle />
        <ShipmentDashboard />
      </div>
    );
  }

  if (dashboardType === 'exports') {
    return (
      <div className="space-y-4">
        <DashboardToggle />
        <ExportsDashboard masterData={masterData} customers={customers} />
      </div>
    );
  }

  // Local Dashboard
  return (
    <div className="space-y-4">
      <DashboardToggle />
      <LocalDashboard masterData={masterData} customers={customers} />
    </div>
  );
};

export default DashboardWrapper;
